import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import { MEMBERSHIPS, fmt } from "@/data/autosCatalog";
import { usePageEvent } from "@/lib/analytics";
import AutosFooter from "@/pages/AutosFooter";

const GOLD = "#C7A668";
const WHATSAPP_NUMBER = "525578988731";

export default function AutosMemberships({ lang: langProp }) {
  const { L } = useLang(); // eslint-disable-line no-unused-vars
  const en = langProp === "en";
  usePageEvent({ event: "membership_interest", lead_type: "membership", business: "dukk" });

  const joinLink = (m) => {
    const text = encodeURIComponent(`${en ? "Hi Dukk, I'm interested in the membership:" : "Hola Dukk, me interesa la membresía:"} ${m[en ? "en" : "es"]}`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  };

  return (
    <div className="bg-[#0A0A0B] text-[#F2EFE8]" style={{ overflowX: "clip" }}>
      <PageSeo esPath="/autos/membresias" enPath="/en/cars/memberships" lang={en ? "en" : "es"}
        title={en ? "Car Care Memberships | Dukk" : "Membresías de Cuidado de Auto | Dukk"}
        description={en ? "Dukk car care memberships in Mexico — scheduled premium mobile washes, module discounts and multi-car Garage plans with assigned technician." : "Membresías de cuidado de auto Dukk en México — lavados premium a domicilio agendados, descuentos en módulos y planes Garaje multi-auto con técnico asignado."} />

      <section className="px-6 md:px-16" style={{ paddingTop: 150, paddingBottom: 60 }}>
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".2em", color: GOLD, marginBottom: 16 }}>{en ? "DUKK · MEMBERSHIPS" : "DUKK · MEMBRESÍAS"}</div>
            <h1 className="font-display" style={{ fontSize: "clamp(34px,5vw,64px)", lineHeight: 1.05, letterSpacing: "-.025em", margin: 0, fontWeight: 300, maxWidth: 900 }}>
              {en ? "The one-time is the door. The membership is the relationship." : "El one-time es la puerta. La membresía es la relación."}
            </h1>
            <p style={{ color: "#C9CED4", fontSize: "clamp(15px,1.6vw,18px)", lineHeight: 1.6, marginTop: 24, maxWidth: 620, fontWeight: 300 }}>
              {en ? "The more cars Dukk cares for at your home, the more you belong to the house. Rewarded with priority, an assigned technician and the extra car included." : "Mientras más autos cuida Dukk de tu casa, más eres de la casa. Recompensado con prioridad, técnico asignado y el auto extra sin costo."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 md:px-16" style={{ paddingBottom: 120 }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3" style={{ gap: 24 }}>
          {MEMBERSHIPS.map((m, i) => (
            <motion.div key={m.key} data-testid={`membership-${m.key}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.07 }}
              className="h-full flex flex-col rounded-sm" style={{ padding: 32, background: m.featured ? "linear-gradient(160deg,#16130c,#0a0a0a 60%)" : "#121214", border: `1px solid ${m.featured ? GOLD : "rgba(199,166,104,.2)"}` }}>
              {m.featured && <div className="font-mono" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "#0a0a0a", background: GOLD, padding: "4px 11px", borderRadius: 999, alignSelf: "flex-start", marginBottom: 18 }}>{en ? "MOST POPULAR" : "MÁS POPULAR"}</div>}
              <div className="flex items-baseline justify-between">
                <div className="font-display" style={{ fontSize: 30, color: m.featured ? GOLD : "#F2EFE8", fontWeight: 300 }}>{m[en ? "en" : "es"]}</div>
                {(m.tagEs || m.tagEn) && <span className="font-mono" style={{ fontSize: 11, color: GOLD, letterSpacing: ".08em" }}>{m[en ? "tagEn" : "tagEs"]}</span>}
              </div>
              <div style={{ marginTop: 12, marginBottom: 20 }}>
                {m.quote ? <span className="font-display" style={{ fontSize: 26, color: "#F2EFE8", fontWeight: 300 }}>{en ? "By quote" : "Cotización"}</span> : (
                  <span><span className="font-display" style={{ fontSize: 34, color: "#F2EFE8", fontWeight: 300 }}>{fmt(m.price)}</span><span className="text-white/45" style={{ fontSize: 14 }}> / {en ? "month" : "mes"}</span></span>
                )}
              </div>
              <div className="space-y-3" style={{ marginBottom: 28 }}>
                {(en ? m.incEn : m.incEs).map((b, j) => (<div key={j} className="flex gap-2.5 items-start"><Check size={15} color={GOLD} strokeWidth={2} style={{ marginTop: 3, flex: "none" }} /><span style={{ color: "#C9CED4", fontSize: 14.5, lineHeight: 1.45, fontWeight: 300 }}>{b}</span></div>))}
              </div>
              <a href={joinLink(m)} target="_blank" rel="noreferrer" data-testid={`membership-cta-${m.key}`} className="no-underline font-mono" style={{ display: "inline-flex", alignItems: "center", gap: 8, alignSelf: "flex-start", color: m.featured ? "#0a0a0a" : GOLD, background: m.featured ? "#F2EFE8" : "transparent", border: m.featured ? "none" : `1px solid ${GOLD}`, borderRadius: 999, padding: "12px 24px", fontSize: 12.5, letterSpacing: ".06em", marginTop: "auto" }}>
                {en ? "BECOME A MEMBER" : "HAZTE MIEMBRO"} <ArrowRight size={14} />
              </a>
            </motion.div>
          ))}
        </div>
        <p className="max-w-7xl mx-auto text-white/40 font-light" style={{ marginTop: 28, fontSize: 13 }}>
          {en ? "Every plan saves you versus buying à la carte. Prices in MXN, tax included where applicable." : "Todo plan te deja ahorrando frente a comprar suelto. Precios en MXN, impuestos incluidos donde aplique."}
        </p>
      </section>

      <AutosFooter lang={langProp} />
    </div>
  );
}
